import React, {
  TextareaHTMLAttributes,
  useState,
  useMemo,
  useRef,
  ChangeEvent,
  KeyboardEventHandler
} from "react";
import classNames from "classnames";
import DropZone from "react-dropzone";
import { BaseEmoji } from "emoji-mart/dist-es/utils/emoji-index/nimble-emoji-index";
import styles from "./chatInputComponent.css";
import { IChatInput, InputType } from "../../models/room/ChatInput";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { FileThumbnail } from "@fidget/common/components/files/fileThumbnail";
import { IFileUploadInfo } from "@fidget/common/models/fileUpload";
import { EmojiPicker } from "@fidget/common/components/emojiPicker";
import { observer } from "mobx-react";
import { useStore } from "../../models";
import { ChatHighlighter } from "./chatInputUtils/chatHighlighter/chatHighlighter";
import { TemplatePart } from "./chatInputUtils/chatInputUtilsTypes";
import {
  Highlight,
  mentionsTemplates
} from "./chatInputUtils/chatHighlighter/chatHighlighterTypes";
import Downshift, { ControllerStateAndHelpers } from "downshift";
import {
  NotificationLevel,
  NotificationType
} from "@fidget/common/models/mst/ui/ChatNotificationStore";
import {
  replaceText,
  TemplatePair,
  templatePairs
} from "../../../src/models/intent/intentStoreUtils";
import { staticConfig } from "@fidget/common/config/static-config";

export interface ChatMessageInputProps {
  inputChatState: IChatInput;
  guestFirstName?: string;
  onMessageTyping?: () => void;
  onSendMessage?: () => void;
  setTextAreaRef?: (el?: HTMLTextAreaElement) => void;
  renderConfirmButtonElement?: () => JSX.Element;
  inputProps?: TextareaHTMLAttributes<HTMLTextAreaElement>;
  onChange?: () => void;
  placeholder?: string;
  hideFileUpload?: boolean; // NOTE: needed temporarily, because of ai suggestions intent response
  setInputScrollRef?: (div: HTMLDivElement) => void;
  isIntentMenuOpened?: boolean;
  activationSequence?: string;
  delimeterSequence?: string;
}

export const ChatMessageInput = observer((props: ChatMessageInputProps) => {
  const {
    inputChatState,
    guestFirstName,
    renderConfirmButtonElement,
    inputProps,
    hideFileUpload,
    placeholder,
    setInputScrollRef,
    isIntentMenuOpened
  } = props;

  const {
    mainScrollContainer,
    setFilesScrollContainer,
    setMainScrollContainer,
    setTextArea,
    isEmojiPickerOpen,
    handleChange,
    handleKeyPress,
    handleInput,
    handleIntentPicked,
    handleEmojiIconClick,
    onFileRemoved,
    onFilesAdded,
    addEmoji,
    messageMaxAllowedCharacters,
    templateParts,
    activeTemplate
  } = chatMessageInputState(props);

  const getPlaceholder = (): string => {
    if (placeholder) {
      return placeholder;
    }

    if (inputChatState.activeInputType === InputType.Note) {
      return intl.formatMessage("conversation.input.placeholder.note");
    }

    if (guestFirstName) {
      return intl.formatMessage("conversation.input.placeholder.message", { name: guestFirstName });
    }

    return intl.formatMessage("conversation.input.placeholder.message.default");
  };

  const getBorderStyle = () => {
    if (!!hideFileUpload) {
      return styles["input-border"];
    }
    if (inputChatState.activeInputType === InputType.Message) {
      return styles["message-border"];
    }
    if (inputChatState.activeInputType === InputType.Note) {
      return styles["note-border"];
    }

    return styles["chat-border"];
  };

  const renderFilesContainer = () => {
    const { fileUploads } = inputChatState;
    const filesInRow = Math.floor(
      (mainScrollContainer && mainScrollContainer.clientWidth / 104) || 1
    );
    const shouldBeScrollable = fileUploads.length > filesInRow * 2;

    return (
      <div
        ref={(el) => setFilesScrollContainer(el as HTMLDivElement)}
        className={classNames(styles["file-preview-area"], shouldBeScrollable && styles.scroll)}
      >
        {fileUploads.map((fileInfo, i) => (
          <FileThumbnail
            fileUploadInfo={fileInfo}
            key={fileInfo.file.name}
            small
            isInScrollable={shouldBeScrollable}
            isInFirstRow={i < filesInRow}
            onRetry={(fileUpload) => inputChatState.retryAttachFile(fileUpload)}
            onRemove={() => onFileRemoved(fileInfo)}
          />
        ))}
      </div>
    );
  };

  const renderEmojiPicker = () => {
    return isEmojiPickerOpen && <EmojiPicker onEmojiPicked={(emoji) => addEmoji(emoji)} />;
  };

  const renderDropZone = () => {
    return (
      inputChatState.activeInputType === InputType.Message && (
        <DropZone
          className={classNames(styles.attachment)}
          onDrop={onFilesAdded}
          activeClassName={styles.active}
        >
          <i className={classNames("icon-attach-dashboard", styles["attachment-icon"])} />
          <div className={styles["dropdown-info"]}>
            <div className={styles["upload-icon"]}>
              <i className={classNames("icon-upload")} />
            </div>
            <div>
              <L10n id={"conversation.input.attachment"} />
            </div>
          </div>
        </DropZone>
      )
    );
  };

  const renderIntentListItem = (
    item: TemplatePair,
    index: number,
    helpers: ControllerStateAndHelpers<TemplatePair>
  ) => {
    const { getItemProps, highlightedIndex, selectedItem } = helpers;
    const highlighted = highlightedIndex === index;
    const selected = !!selectedItem && item.key === selectedItem.key;
    const itemProps = getItemProps({ key: index, index, item });

    return (
      <li {...itemProps}>
        <div
          className={classNames(styles["default-grid"], {
            [styles.highlighted]: highlighted,
            [styles.selected]: selected
          })}
        >
          <div className={styles["template-menu-item"]}>{item.value}</div>
        </div>
      </li>
    );
  };

  const renderIntentListMenu = (helpers: ControllerStateAndHelpers<TemplatePair>) => {
    const { getMenuProps } = helpers;

    const items = templatePairs.filter(({ value }) => value.includes(activeTemplate.text));

    return items.length ? (
      <div className={styles["template-menu"]}>
        <div className={styles["menu-container"]}>
          <ul {...getMenuProps()}>
            {items.map((item, index) => renderIntentListItem(item, index, helpers))}
          </ul>
        </div>
      </div>
    ) : (<></>);
  };

  const renderTextArea = (helpers: ControllerStateAndHelpers<TemplatePair>) => {
    const inputText = replaceText(inputChatState.inputText, templatePairs);

    const currentProps = helpers.isOpen ? (helpers.getInputProps() as any) : inputProps;
    const handleKeyDown = helpers.isOpen
      ? (helpers.getInputProps().onKeyDown as unknown as KeyboardEventHandler<HTMLTextAreaElement>)
      : isIntentMenuOpened
      ? inputProps?.onKeyDown
      : undefined;

    return (
      <div
        className={styles["textarea-emoji-container"]}
        ref={setInputScrollRef ? (el) => setInputScrollRef(el as HTMLDivElement) : undefined}
      >
        <div className={styles["ghost-input"]}>
          <ChatHighlighter text={inputText || " "} parts={templateParts} />

          <textarea
            {...currentProps}
            rows={1}
            ref={(el) => setTextArea(el as HTMLTextAreaElement)}
            placeholder={getPlaceholder()}
            value={inputText}
            onKeyPress={handleKeyPress}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            onInput={handleInput}
            maxLength={messageMaxAllowedCharacters}
          />
        </div>
        <button
          type="button"
          className={classNames(styles["emoji-button"], { [styles.active]: isEmojiPickerOpen })}
          onClick={handleEmojiIconClick}
        >
          <i className="icon-emoji" />
        </button>
      </div>
    );
  };

  const renderInputAutoComplete = () => (
    <Downshift
      isOpen={inputChatState.activeInputType === InputType.Message && activeTemplate.active}
      onChange={(intent, helpers) => handleIntentPicked(intent || undefined, helpers)}
      itemToString={(item) => item?.value ?? ""}
    >
      {(helpers: ControllerStateAndHelpers<TemplatePair>) => (
        <div className={styles["template-menu-container"]}>
          {renderTextArea(helpers)}
          {helpers.isOpen && renderIntentListMenu(helpers)}
        </div>
      )}
    </Downshift>
  );

  const renderFilesZone = () => {
    const { activeInputType, hasFiles } = inputChatState;
    const canAttachFiles = activeInputType === InputType.Message;
    return (
      canAttachFiles &&
      hasFiles && (
        <DropZone
          disableClick
          onDrop={onFilesAdded}
          className={classNames(styles["drop-zone"])}
          activeClassName={styles["active-drop-zone"]}
        >
          {renderFilesContainer()}
          <div className={styles["dropdown-info"]}>
            <div className={styles["upload-icon"]}>
              <i className="icon-upload" />
            </div>
            <div>
              <L10n id="conversation.input.attachment" />
            </div>
          </div>
        </DropZone>
      )
    );
  };

  const render = () => {
    const { activeInputType } = inputChatState;

    return (
      <div className={styles["input-and-button"]}>
        {!hideFileUpload && renderDropZone()}
        <div
          className={classNames(styles["input-wrap"], getBorderStyle(), {
            [styles["in-note"]]: activeInputType === InputType.Note,
            [styles["in-message"]]: activeInputType === InputType.Message,
            [styles["no-send-button"]]: !renderConfirmButtonElement,
            [styles["no-files-upload"]]: hideFileUpload
          })}
          ref={(el) => setMainScrollContainer(el as HTMLDivElement)}
        >
          {renderFilesZone()}
          {renderInputAutoComplete()}
        </div>
        {renderConfirmButtonElement && renderConfirmButtonElement()}
        {renderEmojiPicker()}
      </div>
    );
  };

  return render();
});

interface State {
  mainScrollContainer?: HTMLDivElement;
  setFilesScrollContainer: (d: HTMLDivElement) => void;
  setMainScrollContainer: (d: HTMLDivElement) => void;
  setTextArea: (e: HTMLTextAreaElement) => void;
  isEmojiPickerOpen: boolean;
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  handleInput: () => void;
  handleIntentPicked: (
    intent?: TemplatePair,
    helpers?: ControllerStateAndHelpers<TemplatePair>
  ) => void;
  onFileRemoved: (fileInfo: IFileUploadInfo) => void;
  handleEmojiIconClick: () => void;
  addEmoji: (emoji: BaseEmoji) => void;
  onFilesAdded: (acceptedFiles: File[]) => void;
  maxAttachmentSizeBytes: number;
  messageMaxAllowedCharacters: number;
  templateParts: TemplatePart<Highlight>[];
  activeTemplate: { active: boolean; text: string };
}

const chatMessageInputState = (props: ChatMessageInputProps): State => {
  const {
    uiStore,
    profanityStore,
    currentUserStore: { orgConfig },
    roomStore,
  } = useStore();
  const { onSendMessage, activationSequence, delimeterSequence } = props;
  const [filesScrollContainer, setFilesScrollContainer] = useState<HTMLDivElement>();
  const [mainScrollContainer, setMainScrollContainer] = useState<HTMLDivElement>();
  const [textAreaElement, setTextAreaElement] = useState<HTMLTextAreaElement>();
  const [isEmojiPickerOpen, setEmojiPickerOpen] = useState<boolean>(false);
  const [activeTemplate, setActiveTemplate] = useState({ active: false, text: "" });
  const cursorPosition = useRef(0);

  const { messageText } = props.inputChatState;
  const { profanities } = profanityStore;

  const templateParts = useMemo(
    () => [
      ...profanities.map(
        (profanity): TemplatePart<Highlight> => ({
          key: profanity,
          part: { type: "PROFANITY", text: profanity }
        })
      ),
      ...mentionsTemplates
    ],
    [profanities, mentionsTemplates]
  );

  const maxAttachmentSizeBytes = orgConfig?.maximumAttachmentSizeBytes || 0;
  const messageMaxAllowedCharacters =
    orgConfig?.messageMaxAllowedCharactersForAgent ??
    staticConfig.maxMessageAllowedCharactersForAgent;

  // This handler takes care of recognizing correct character sequences
  const handleActivationChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (!activationSequence || !delimeterSequence) {
      return;
    }
    const cursorIndex = e.target.selectionStart;
    cursorPosition.current = cursorIndex;

    const value = e.target.value;

    const textBeforeCursor = value.substr(0, cursorIndex);
    const closestDelimeterSequenceIndex = textBeforeCursor.lastIndexOf(delimeterSequence);
    const textBetweenDelimeterAndCursor =
      closestDelimeterSequenceIndex !== -1
        ? textBeforeCursor.substr(closestDelimeterSequenceIndex, cursorIndex)
        : textBeforeCursor;
    const activationSequenceIndex = textBetweenDelimeterAndCursor.indexOf(activationSequence);

    if (activationSequenceIndex === -1) {
      setActiveTemplate({ active: false, text: "" });
      return;
    }

    const textBetweenActivationAndCursor = textBetweenDelimeterAndCursor.substr(
      activationSequenceIndex + activationSequence.length
    );

    setActiveTemplate({ active: true, text: textBetweenActivationAndCursor });
  };

  const handleIntentPicked = (
    intent?: TemplatePair,
    helpers?: ControllerStateAndHelpers<TemplatePair>
  ) => {
    const textBeforeIntent = messageText.substr(
      0,
      cursorPosition.current - activeTemplate.text.length - (activationSequence?.length ?? 0)
    );
    const textIntent = (intent?.value ?? "") + " ";
    const textAfterIntent = messageText.substr(cursorPosition.current, messageText.length);

    const textToSet = textBeforeIntent + textIntent + textAfterIntent;
    props.inputChatState.setMessageText(textToSet);

    const cursorOffset = textBeforeIntent.length + textIntent.length;
    textAreaElement?.setSelectionRange(cursorOffset, cursorOffset);

    setActiveTemplate({ active: false, text: "" });
    helpers?.reset({ selectedItem: undefined });
  };

  const setTextArea = (el?: HTMLTextAreaElement) => {
    const { setTextAreaRef: propsSetTextArea } = props;
    setTextAreaElement(el);
    if (propsSetTextArea) {
      propsSetTextArea(el);
    }
  };

  const compareTextToLimit = (text: string) => {
    if (onSendMessage) {
      if (text.length >= messageMaxAllowedCharacters) {
        uiStore.setChatNotification({
          message: intl.formatMessage("conversation.warning.inputLength.title", {
            limit: messageMaxAllowedCharacters
          }),
          type: NotificationType.ChatMessageLength,
          level: NotificationLevel.Warning,
          onAction: () => onSendMessage && onSendMessage(),
          onCancel: () => textAreaElement?.focus(),
          actionText: intl.formatMessage("conversation.warning.inputLength.action")
        });
      } else {
        uiStore.removeChatNotificationIfType(NotificationType.ChatMessageLength);
      }
    }

    return text.substring(0, messageMaxAllowedCharacters);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const {
      target: { value }
    } = e;
    const {
      inputChatState: {
        activeInputType,
        setMessageText,
        setNoteText,
        foundProfanity,
        unsetFoundProfanity
      },
      inputProps,
      onChange
    } = props;
    const text = compareTextToLimit(value);

    if (activeInputType === InputType.Message) {
      setMessageText(text);
    } else {
      setNoteText(text);
    }

    if (foundProfanity && !profanityStore.isProfanityIncludedInText(foundProfanity, value)) {
      unsetFoundProfanity();
      uiStore.removeChatNotificationIfType(NotificationType.ChatProfanity);
    }

    if (inputProps && inputProps.onChange) {
      inputProps.onChange(e);
    }

    if (onChange) {
      onChange();
    }

    handleActivationChange(e);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      (event.nativeEvent.key === "Enter" || event.nativeEvent.key === "Return") &&
      !event.nativeEvent.shiftKey &&
      !event.nativeEvent.ctrlKey
    ) {
      event.preventDefault();
      if (onSendMessage) {
        onSendMessage();
      }
    }
  };

  const handleInput = () => {
    const { onMessageTyping } = props;

    scrollInputToBottom(mainScrollContainer);

    if (props.inputChatState.activeInputType === InputType.Message && onMessageTyping) {
      onMessageTyping();
    }
  };

  const onFileRemoved = (fileInfo: IFileUploadInfo) => {
    const { inputChatState, onChange } = props;
    inputChatState.cancelAttachFile(fileInfo);
    if (onChange) {
      onChange();
    }
  };

  const handleEmojiIconClick = () => {
    setEmojiPickerOpen((current) => !current);
  };

  const addEmoji = (emoji: BaseEmoji) => {
    const newText = props.inputChatState.inputText + emoji.native;
    const isMessageInput = props.inputChatState.activeInputType === InputType.Message;
    const isMessageLimited = isMessageInput && compareTextToLimit(newText) !== newText;
    if (!isMessageLimited) {
      props.inputChatState.appendText(emoji.native);
    }
    setEmojiPickerOpen(false);
    if (textAreaElement) {
      textAreaElement.focus();
    }
  };

  const onFilesAdded = (acceptedFiles: File[]) => {
    const { inputChatState, onChange } = props;
    const roomId = roomStore.currentRoom?.id!;

    acceptedFiles.forEach((file) => {
      inputChatState.attachFile(file, roomId);
      scrollInputToBottom(filesScrollContainer);
    });
    if (textAreaElement) {
      textAreaElement.focus();
    }
    if (onChange) {
      onChange();
    }
  };

  const scrollInputToBottom = (scroll?: HTMLDivElement) => {
    if (scroll) {
      scroll.scrollTop = scroll.scrollHeight - scroll.offsetHeight;
    }
  };

  return {
    mainScrollContainer,
    setFilesScrollContainer,
    setMainScrollContainer,
    setTextArea,
    isEmojiPickerOpen,
    handleChange,
    handleKeyPress,
    handleInput,
    handleIntentPicked,
    onFileRemoved,
    handleEmojiIconClick,
    addEmoji,
    onFilesAdded,
    maxAttachmentSizeBytes,
    messageMaxAllowedCharacters,
    templateParts,
    activeTemplate
  };
};
